import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../shared/storage/firebase';
import { User } from '../../../types';

type SquadEvent = {
  id: string;
  squadId?: string;
  title?: string;
  start?: any; // Firestore Timestamp or Date
  end?: any;
  mainTeacherId?: string | null;
};

const getDateFromAny = (d: any): Date | null => {
  if (!d) return null;
  if (d instanceof Date) return d;
  if (typeof d.toDate === 'function') return d.toDate();
  try {
    return new Date(d);
  } catch {
    return null;
  }
};

const DAY_MS = 24 * 60 * 60 * 1000;
const startOfWeekUTC = (d: Date) => {
  // Normalize to UTC midnight
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  // Monday as first day of week
  const dow = date.getUTCDay(); // 0=Sun..6=Sat
  const diff = (dow + 6) % 7; // 0 for Mon, 6 for Sun
  date.setUTCDate(date.getUTCDate() - diff);
  return date;
};
const biweekKey = (d: Date) => {
  // Anchor to a known Monday (1970-01-05)
  const baseMonday = new Date(Date.UTC(1970, 0, 5));
  const monday = startOfWeekUTC(d);
  const weeksFromBase = Math.floor((monday.getTime() - baseMonday.getTime()) / (7 * DAY_MS));
  const biweekIndex = Math.floor(weeksFromBase / 2);
  const periodStart = new Date(baseMonday.getTime() + biweekIndex * 14 * DAY_MS);
  const y = periodStart.getUTCFullYear();
  const m = String(periodStart.getUTCMonth() + 1).padStart(2, '0');
  const day = String(periodStart.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`; // biweekly period starting Monday
};

const formatUSD = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const PAY_PER_CLASS = 70;

const TeachersOverviewTab: React.FC = () => {
  const [teachers, setTeachers] = useState<User[]>([]);
  const [events, setEvents] = useState<SquadEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      try {
        // Load teachers
        const teachersQuery = query(collection(db, 'users'), where('role', '==', 'teacher'));
        const teacherSnap = await getDocs(teachersQuery);
        const teacherList = teacherSnap.docs.map(d => ({ ...(d.data() as any), id: d.id })) as User[];
        setTeachers(teacherList);

        // Load all squad events
        const evSnap = await getDocs(collection(db, 'squadEvents'));
        const evts = evSnap.docs.map(d => ({ id: d.id, ...(d.data() as any) })) as SquadEvent[];
        setEvents(evts);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, []);

  const now = new Date();

  const { periodsSorted, perTeacherTaught, perTeacherAssigned, totalsByTeacher, totalsByPeriod } = useMemo(() => {
    const periodsSet = new Set<string>(); // include only periods that already happened (taught events exist)
    const perTeacherTaught = new Map<string, Map<string, number>>();
    const perTeacherAssigned = new Map<string, Map<string, number>>();

    events.forEach(ev => {
      const start = getDateFromAny(ev.start);
      if (!start) return;
      const pKey = biweekKey(start);
      const teacherId = ev.mainTeacherId || '';
      if (!teacherId) return;

      // assigned (kept for potential future use, but we don't add future periods to header)
      const aMap = perTeacherAssigned.get(teacherId) || new Map<string, number>();
      aMap.set(pKey, (aMap.get(pKey) || 0) + 1);
      perTeacherAssigned.set(teacherId, aMap);

      // taught (only if in the past)
      if (start.getTime() <= now.getTime()) {
        // Only include periods that already happened
        periodsSet.add(pKey);
        const tMap = perTeacherTaught.get(teacherId) || new Map<string, number>();
        tMap.set(pKey, (tMap.get(pKey) || 0) + 1);
        perTeacherTaught.set(teacherId, tMap);
      }
    });

    const periodsSorted = Array.from(periodsSet).sort();

    const totalsByTeacher = new Map<
      string,
      { taughtTotal: number; owedTotal: number; assignedTotal: number }
    >();
    teachers.forEach(t => {
      const taughtMap = perTeacherTaught.get(t.id) || new Map();
      const assignedMap = perTeacherAssigned.get(t.id) || new Map();
      const taughtTotal = Array.from(taughtMap.values()).reduce((a, b) => a + b, 0);
      const assignedTotal = Array.from(assignedMap.values()).reduce((a, b) => a + b, 0);
      totalsByTeacher.set(t.id, {
        taughtTotal,
        owedTotal: taughtTotal * PAY_PER_CLASS,
        assignedTotal
      });
    });

    // Calculate totals by period
    const totalsByPeriod = new Map<string, { taughtTotal: number; costTotal: number }>();
    periodsSorted.forEach(period => {
      let taughtTotal = 0;
      teachers.forEach(t => {
        const taughtMap = perTeacherTaught.get(t.id) || new Map();
        taughtTotal += taughtMap.get(period) || 0;
      });
      totalsByPeriod.set(period, {
        taughtTotal,
        costTotal: taughtTotal * PAY_PER_CLASS
      });
    });

    return { periodsSorted, perTeacherTaught, perTeacherAssigned, totalsByTeacher, totalsByPeriod };
  }, [events, teachers, now]);

  if (loading) {
    return <div className="admin-content-card">Loading teacher stats…</div>;
  }

  return (
    <div className="teachers-overview-tab" style={{ width: '100%' }}>
      <div className="admin-content-card" style={{ display: 'block' }}>
        <h3 style={{ marginTop: 0 }}>👩‍🏫 Teacher Payroll Overview</h3>
        <p style={{ color: '#6b7280', marginTop: 0 }}>
          Each biweekly period shows classes taught and the corresponding owed amount at ${PAY_PER_CLASS} per class.
          We infer assignments and taught classes from calendar events (mainTeacherId).
        </p>
        <div className="table-wrapper" style={{ width: '100%', overflowX: 'auto' }}>
          <table className="reservations-table" style={{ minWidth: 800 }}>
            <thead>
              <tr>
                <th className="sticky-col" style={{ minWidth: 220 }}>Teacher</th>
                {periodsSorted.map(p => (
                  <th key={p}>{p}</th>
                ))}
                <th>Total Taught</th>
                <th style={{ minWidth: 140 }}>Total Owed</th>
              </tr>
            </thead>
            <tbody>
              {/* Totals row */}
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <td className="sticky-col" style={{ minWidth: 220, fontWeight: 700 }}>
                  Total
                </td>
                {periodsSorted.map(p => {
                  const periodTotals = totalsByPeriod.get(p) || { taughtTotal: 0, costTotal: 0 };
                  return (
                    <td key={p} style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 700 }}>{periodTotals.taughtTotal}</div>
                      <div style={{ color: '#6b7280', whiteSpace: 'nowrap', fontWeight: 600 }}>
                        {formatUSD(periodTotals.costTotal)}
                      </div>
                    </td>
                  );
                })}
                <td style={{ textAlign: 'right', fontWeight: 700 }}>
                  {Array.from(totalsByPeriod.values()).reduce((sum, t) => sum + t.taughtTotal, 0)}
                </td>
                <td style={{ textAlign: 'right', fontWeight: 700, minWidth: 140 }}>
                  {formatUSD(
                    Array.from(totalsByPeriod.values()).reduce((sum, t) => sum + t.costTotal, 0)
                  )}
                </td>
              </tr>
              {teachers
                .slice()
                .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''))
                .map(t => {
                  const taughtMap = perTeacherTaught.get(t.id) || new Map<string, number>();
                  const totals = totalsByTeacher.get(t.id) || { taughtTotal: 0, owedTotal: 0, assignedTotal: 0 };
                  if (!totals.owedTotal) return null;
                  return (
                    <tr key={t.id}>
                      <td className="sticky-col" style={{ minWidth: 220, whiteSpace: 'normal' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
                          <span style={{ fontWeight: 600 }}>{t.displayName || t.id}</span>
                        </div>
                      </td>
                      {periodsSorted.map(p => {
                        const taught = taughtMap.get(p) || 0;
                        const owed = taught * PAY_PER_CLASS;
                        return (
                          <td key={p} style={{ textAlign: 'right' }}>
                            <div style={{ fontWeight: 600 }}>{taught}</div>
                            <div style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>{formatUSD(owed)}</div>
                          </td>
                        );
                      })}
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{totals.taughtTotal}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700, minWidth: 140 }}>
                        {formatUSD(totals.owedTotal)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TeachersOverviewTab;


